import React from 'react';
import { motion } from 'framer-motion';

const SkeletonLoader = ({ 
  variant = 'text', 
  lines = 1, 
  height = 'h-4', 
  width = 'w-full',
  className = '',
  animate = true 
}) => {
  const shimmerAnimation = {
    initial: { x: '-100%' },
    animate: { x: '100%' },
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: [0.4, 0.0, 0.2, 1]
    }
  };

  const pulseAnimation = {
    animate: {
      opacity: [0.5, 1, 0.5],
    },
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: [0.4, 0.0, 0.2, 1]
    }
  };

  const renderSkeleton = () => {
    switch (variant) {
      case 'text':
        return (
          <div className={`space-y-2 ${className}`}>
            {Array.from({ length: lines }).map((_, index) => (
              <div
                key={index}
                className={`relative overflow-hidden bg-gray-200 dark:bg-gray-700 rounded ${height} ${width} ${
                  index === lines - 1 ? 'w-3/4' : 'w-full'
                }`}
              >
                {animate && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    {...shimmerAnimation}
                  />
                )}
              </div>
            ))}
          </div>
        );

      case 'card':
        return (
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 ${className}`}>
            <div className="flex items-center space-x-4 mb-4">
              <div className="relative overflow-hidden w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full">
                {animate && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    {...shimmerAnimation}
                  />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4">
                  {animate && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      {...shimmerAnimation}
                    />
                  )}
                </div>
                <div className="relative overflow-hidden h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2">
                  {animate && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      {...shimmerAnimation}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="relative overflow-hidden h-3 bg-gray-200 dark:bg-gray-700 rounded w-full">
                {animate && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    {...shimmerAnimation}
                  />
                )}
              </div>
              <div className="relative overflow-hidden h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6">
                {animate && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    {...shimmerAnimation}
                  />
                )}
              </div>
            </div>
          </div>
        );

      case 'table':
        return (
          <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden ${className}`}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative overflow-hidden h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3">
                {animate && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    {...shimmerAnimation}
                  />
                )}
              </div>
            </div>
            <div className="p-4 space-y-3">
              {Array.from({ length: lines }).map((_, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="relative overflow-hidden w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full">
                    {animate && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        {...shimmerAnimation}
                      />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4">
                      {animate && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          {...shimmerAnimation}
                        />
                      )}
                    </div>
                    <div className="relative overflow-hidden h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/6">
                      {animate && (
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          {...shimmerAnimation}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'avatar':
        return (
          <div className={`relative overflow-hidden ${width} ${height} bg-gray-200 dark:bg-gray-700 rounded-full ${className}`}>
            {animate && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                {...shimmerAnimation}
              />
            )}
          </div>
        );

      case 'button':
        return (
          <div className={`relative overflow-hidden ${width} ${height} bg-gray-200 dark:bg-gray-700 rounded-lg ${className}`}>
            {animate && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                {...shimmerAnimation}
              />
            )}
          </div>
        );

      case 'image':
        return (
          <div className={`relative overflow-hidden ${width} ${height} bg-gray-200 dark:bg-gray-700 rounded-lg ${className}`}>
            {animate && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                {...shimmerAnimation}
              />
            )}
          </div>
        );

      default:
        return (
          <div className={`relative overflow-hidden ${width} ${height} bg-gray-200 dark:bg-gray-700 rounded ${className}`}>
            {animate && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                {...shimmerAnimation}
              />
            )}
          </div>
        );
    }
  };

  return (
    <motion.div
      initial={animate ? pulseAnimation.animate : {}}
      animate={animate ? pulseAnimation.animate : {}}
      transition={animate ? pulseAnimation.transition : {}}
    >
      {renderSkeleton()}
    </motion.div>
  );
};

// Predefined skeleton layouts
export const PageSkeleton = () => (
  <div className="w-full max-w-4xl mx-auto p-6 space-y-6">
    <SkeletonLoader variant="text" lines={1} height="h-8" width="w-1/3" />
    <SkeletonLoader variant="text" lines={3} height="h-4" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, index) => (
        <SkeletonLoader key={index} variant="card" />
      ))}
    </div>
  </div>
);

export const DashboardSkeleton = () => (
  <div className="w-full max-w-6xl mx-auto p-6 space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <SkeletonLoader variant="text" lines={1} height="h-8" width="w-1/4" />
      <SkeletonLoader variant="button" height="h-10" width="w-32" />
    </div>
    
    {/* Stats Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
          <SkeletonLoader variant="text" lines={1} height="h-4" width="w-1/2" />
          <SkeletonLoader variant="text" lines={1} height="h-8" width="w-1/3" className="mt-2" />
        </div>
      ))}
    </div>
    
    {/* Content Cards */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <SkeletonLoader variant="card" />
      <SkeletonLoader variant="card" />
    </div>
  </div>
);

export const LeaderboardSkeleton = () => (
  <div className="w-full max-w-4xl mx-auto flex flex-col items-center bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-8">
    {/* Header Skeleton */}
    <div className="text-center mb-4">
      <SkeletonLoader variant="text" height="h-10" width="w-64" className="mx-auto mb-4" />
      <SkeletonLoader variant="text" height="h-5" width="w-80" className="mx-auto" />
    </div>

    {/* OwlTalkBox Skeleton */}
    <div className="mb-6">
      <SkeletonLoader variant="card" height="h-16" width="w-96" />
    </div>

    {/* Tab Navigation Skeleton */}
    <div className="flex space-x-2 mb-6 w-full max-w-md">
      <SkeletonLoader variant="button" height="h-12" width="w-1/2" />
      <SkeletonLoader variant="button" height="h-12" width="w-1/2" />
    </div>

    {/* Current User Stats Skeleton */}
    <div className="w-full mb-6 p-4 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 rounded-2xl border-2 border-purple-200 dark:border-purple-700">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SkeletonLoader variant="avatar" height="h-8" width="w-8" />
          <div>
            <SkeletonLoader variant="text" height="h-5" width="w-24" className="mb-1" />
            <SkeletonLoader variant="text" height="h-3" width="w-16" />
          </div>
        </div>
        <div className="flex gap-4">
          <div className="text-center">
            <SkeletonLoader variant="text" height="h-6" width="w-8" className="mx-auto mb-1" />
            <SkeletonLoader variant="text" height="h-3" width="w-12" className="mx-auto" />
          </div>
          <div className="text-center">
            <SkeletonLoader variant="text" height="h-6" width="w-8" className="mx-auto mb-1" />
            <SkeletonLoader variant="text" height="h-3" width="w-12" className="mx-auto" />
          </div>
        </div>
      </div>
    </div>

    {/* Leaderboard Table Skeleton */}
    <div className="w-full">
      <div className="text-center mb-4">
        <SkeletonLoader variant="text" height="h-6" width="w-48" className="mx-auto mb-2" />
        <SkeletonLoader variant="text" height="h-4" width="w-64" className="mx-auto" />
      </div>
      
      <div className="bg-gradient-to-r from-yellow-100 to-blue-100 dark:from-blue-900 dark:to-yellow-700 rounded-2xl shadow p-6 w-full">
        {/* Table Header Skeleton */}
        <div className="mb-4">
          <div className="grid grid-cols-4 gap-4">
            <SkeletonLoader variant="text" height="h-6" width="w-16" />
            <SkeletonLoader variant="text" height="h-6" width="w-24" />
            <SkeletonLoader variant="text" height="h-6" width="w-16" />
            <SkeletonLoader variant="text" height="h-6" width="w-16" />
          </div>
        </div>
        
        {/* Table Rows Skeleton */}
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex items-center gap-4 py-2">
              <SkeletonLoader variant="text" height="h-6" width="w-12" />
              <div className="flex items-center gap-2 flex-1">
                <SkeletonLoader variant="avatar" height="h-6" width="w-6" />
                <SkeletonLoader variant="text" height="h-5" width="w-24" />
              </div>
              <SkeletonLoader variant="text" height="h-6" width="w-12" />
              <SkeletonLoader variant="text" height="h-6" width="w-12" />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Motivational Message Skeleton */}
    <div className="mt-6 text-center">
      <SkeletonLoader variant="text" height="h-4" width="w-72" className="mx-auto" />
    </div>
  </div>
);

export const SettingsSkeleton = () => (
  <div className="w-full max-w-4xl mx-auto p-6 space-y-6">
    <SkeletonLoader variant="text" lines={1} height="h-8" width="w-1/4" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {Array.from({ length: 4 }).map((_, index) => (
        <SkeletonLoader key={index} variant="card" />
      ))}
    </div>
  </div>
);

export const CalendarSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-8">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <SkeletonLoader variant="text" height="h-10" width="w-80" className="mx-auto mb-4" />
        <SkeletonLoader variant="text" height="h-5" width="w-96" className="mx-auto" />
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <SkeletonLoader variant="text" height="h-8" width="w-48" className="mb-2" />
            <SkeletonLoader variant="text" height="h-4" width="w-64" />
          </div>
          <div className="flex gap-3">
            <SkeletonLoader variant="button" height="h-10" width="w-32" />
            <SkeletonLoader variant="button" height="h-10" width="w-20" />
          </div>
        </div>
        
        <div className="flex items-center justify-between mb-6">
          <SkeletonLoader variant="button" height="h-8" width="w-8" />
          <SkeletonLoader variant="text" height="h-6" width="w-32" />
          <SkeletonLoader variant="button" height="h-8" width="w-8" />
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-4">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <SkeletonLoader key={i} variant="text" height="h-12" width="w-full" />
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 42 }, (_, i) => (
            <SkeletonLoader key={i} variant="card" height="h-20" width="w-full" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const HabitSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
    <div className="container mx-auto px-4 py-6">
      {/* Header Skeleton */}
      <div className="text-center mb-6">
        <SkeletonLoader variant="text" height="h-10" width="w-64" className="mx-auto mb-2" />
        <SkeletonLoader variant="text" height="h-5" width="w-80" className="mx-auto" />
      </div>

      {/* Stats Row Skeleton */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="text-center">
              <SkeletonLoader variant="text" height="h-8" width="w-16" className="mx-auto mb-2" />
              <SkeletonLoader variant="text" height="h-3" width="w-20" className="mx-auto" />
            </div>
          ))}
        </div>
      </div>

      {/* Tabs Skeleton */}
      <div className="flex justify-center mb-6">
        <div className="flex gap-2 bg-white dark:bg-gray-800 rounded-lg p-1 shadow-md">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonLoader key={index} variant="button" height="h-10" width="w-24" />
          ))}
        </div>
      </div>

      {/* Content Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <SkeletonLoader variant="text" height="h-6" width="w-32" className="mb-4" />
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <SkeletonLoader key={index} variant="card" />
            ))}
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <SkeletonLoader variant="text" height="h-6" width="w-40" className="mb-4" />
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_, index) => (
              <SkeletonLoader key={index} variant="card" />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const AdvancedFeaturesSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
    <div className="container mx-auto px-4 py-6">
      {/* Header Skeleton */}
      <div className="text-center mb-6">
        <SkeletonLoader variant="text" height="h-10" width="w-80" className="mx-auto mb-2" />
        <SkeletonLoader variant="text" height="h-5" width="w-96" className="mx-auto" />
      </div>

      {/* Stats Row Skeleton */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="grid grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="text-center">
              <SkeletonLoader variant="text" height="h-8" width="w-16" className="mx-auto mb-2" />
              <SkeletonLoader variant="text" height="h-3" width="w-20" className="mx-auto" />
            </div>
          ))}
        </div>
        
        {/* Demo Button Skeleton */}
        <div className="mt-4 text-center">
          <SkeletonLoader variant="button" height="h-10" width="w-32" className="mx-auto" />
        </div>
      </div>

      {/* Content Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <SkeletonLoader variant="text" height="h-6" width="w-32" className="mb-4" />
            <div className="space-y-3">
              <SkeletonLoader variant="text" height="h-4" width="w-full" />
              <SkeletonLoader variant="text" height="h-4" width="w-3/4" />
              <SkeletonLoader variant="text" height="h-4" width="w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ReadingSkeleton = () => (
  <div className="min-h-screen pt-20 px-4">
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="relative overflow-hidden h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mx-auto mb-4">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
        <div className="relative overflow-hidden h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mx-auto">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
      </div>

      {/* Upload Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="space-y-6">
          {/* Subject Selection */}
          <div>
            <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
            <div className="relative overflow-hidden h-10 bg-gray-200 dark:bg-gray-700 rounded w-full">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
          </div>

          {/* PDF Upload */}
          <div>
            <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
              <div className="text-center">
                <div className="relative overflow-hidden w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded mx-auto mb-4">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
                <div className="relative overflow-hidden h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto mb-2">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
                <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Start Button */}
          <div className="relative overflow-hidden h-12 bg-gray-200 dark:bg-gray-700 rounded w-full">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
        </div>
      </div>

      {/* Features Preview */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg">
            <div className="relative overflow-hidden w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded mb-2">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
            <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-1">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
            <div className="relative overflow-hidden h-3 bg-gray-200 dark:bg-gray-700 rounded w-full">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Quiz Skeleton
export const QuizSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
    <div className="container mx-auto px-4 py-6">
      <div className="max-w-6xl mx-auto">
        {/* Header Skeleton */}
        <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative overflow-hidden w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div>
                <div className="relative overflow-hidden h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-2">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
                <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-64">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
              </div>
            </div>
            <div className="relative overflow-hidden h-10 bg-gray-200 dark:bg-gray-700 rounded w-32">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
          </div>
        </div>

        {/* Quiz Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <div className="relative overflow-hidden h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Upload Skeleton
export const UploadSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
    {/* Header Skeleton */}
    <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg shadow-lg">
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative overflow-hidden w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
            <div>
              <div className="relative overflow-hidden h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-2">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-80">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Main Content Skeleton */}
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Configuration Skeleton */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8">
        <div className="relative overflow-hidden h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index}>
              <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div className="relative overflow-hidden h-10 bg-gray-200 dark:bg-gray-700 rounded w-full">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upload Area Skeleton */}
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center">
        <div className="relative overflow-hidden w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded mx-auto mb-4">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
        <div className="relative overflow-hidden h-6 bg-gray-200 dark:bg-gray-700 rounded w-64 mx-auto mb-2">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
        <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-96 mx-auto">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
      </div>
    </div>
  </div>
);

// Flashcards Skeleton
export const FlashcardsSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 dark:from-gray-900 dark:to-gray-800">
    {/* Header Skeleton */}
    <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg shadow-lg">
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative overflow-hidden w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
            <div>
              <div className="relative overflow-hidden h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-2">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-80">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Main Content Skeleton */}
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Quick Actions Skeleton */}
      <div className="mb-8">
        <div className="relative overflow-hidden h-6 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-4">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
            <div className="flex items-center gap-4">
              <div className="relative overflow-hidden w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div className="flex-1">
                <div className="relative overflow-hidden h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
                <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-full">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chapter Flashcards Skeleton */}
      <div>
        <div className="relative overflow-hidden h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-6">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <div className="relative overflow-hidden h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Study Content Skeleton
export const StudyContentSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Skeleton */}
        <div className="mb-8">
          <div className="relative overflow-hidden h-6 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-4">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
          <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative overflow-hidden w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  animate={{ x: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                />
              </div>
              <div>
                <div className="relative overflow-hidden h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-2">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
                <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-80">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-6">
          <div className="space-y-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index}>
                <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
                <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Login Skeleton
export const LoginSkeleton = () => (
  <div className="min-h-screen flex flex-col bg-white dark:bg-black">
    {/* Header Skeleton */}
    <header className="w-full flex flex-col items-center justify-center pt-2 pb-1">
      <div className="flex items-center justify-center">
        <div className="relative overflow-hidden h-24 w-24 bg-gray-200 dark:bg-gray-700 rounded shadow-xl">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
      </div>
    </header>

    {/* Main Content Skeleton */}
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md mx-auto flex flex-col items-center bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-6">
        <div className="w-full flex flex-col gap-3">
          {/* Description Text Skeleton */}
          <div className="text-center mb-1">
            <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-80 mx-auto">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
          </div>

          {/* Google Button Skeleton */}
          <div className="relative overflow-hidden h-12 bg-gray-200 dark:bg-gray-700 rounded-full w-full">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>

          {/* Instagram Button Skeleton */}
          <div className="relative overflow-hidden h-12 bg-gray-200 dark:bg-gray-700 rounded-full w-full">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>

          {/* Email Button Skeleton */}
          <div className="relative overflow-hidden h-12 bg-gray-200 dark:bg-gray-700 rounded-full w-full">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>

          {/* Mobile Button Skeleton */}
          <div className="relative overflow-hidden h-12 bg-gray-200 dark:bg-gray-700 rounded-full w-full">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Role Selection Skeleton
export const RoleSelectionSkeleton = () => (
  <div className="min-h-screen flex flex-col bg-white dark:bg-black">
    {/* Header Skeleton */}
    <header className="w-full flex flex-col items-center justify-center pt-2 pb-1">
      <div className="flex items-center justify-center">
        <div className="relative overflow-hidden h-24 w-24 bg-gray-200 dark:bg-gray-700 rounded shadow-xl">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
      </div>
    </header>

    {/* Main Content Skeleton */}
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md mx-auto flex flex-col items-center bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-8 mt-8">
        {/* Title Skeleton */}
        <div className="text-center mb-6">
          <div className="relative overflow-hidden h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mx-auto mb-2">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
          <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-64 mx-auto">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
        </div>

        {/* Button Skeletons */}
        <div className="w-full space-y-4">
          <div className="relative overflow-hidden h-14 bg-gray-200 dark:bg-gray-700 rounded-xl w-full">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
          
          <div className="relative overflow-hidden h-14 bg-gray-200 dark:bg-gray-700 rounded-xl w-full">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Course Selection Skeleton
export const CourseSelectionSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
    <div className="w-full max-w-4xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        {/* Progress Steps Skeleton */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className="relative overflow-hidden w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                  />
                </div>
                {step < 3 && (
                  <div className="relative overflow-hidden w-16 h-1 bg-gray-200 dark:bg-gray-700 mx-2">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      initial={{ x: '-100%' }}
                      animate={{ x: '100%' }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mx-auto">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
          </div>
        </div>

        {/* Header Skeleton */}
        <div className="text-center mb-6">
          <div className="relative overflow-hidden h-10 bg-gray-200 dark:bg-gray-700 rounded w-80 mx-auto mb-2">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
          <div className="relative overflow-hidden h-5 bg-gray-200 dark:bg-gray-700 rounded w-96 mx-auto mb-4">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
          
          {/* User Info Skeleton */}
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full px-4 py-2">
            <div className="relative overflow-hidden w-6 h-6 bg-white/20 rounded-full">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
            <div className="relative overflow-hidden h-4 bg-white/20 rounded w-24">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
              />
            </div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
          {/* Title Skeleton */}
          <div className="relative overflow-hidden h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mx-auto mb-6">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
          
          {/* Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <div className="relative overflow-hidden w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto mb-4">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      initial={{ x: '-100%' }}
                      animate={{ x: '100%' }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                    />
                  </div>
                  <div className="relative overflow-hidden h-6 bg-gray-200 dark:bg-gray-700 rounded w-24 mx-auto mb-2">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      initial={{ x: '-100%' }}
                      animate={{ x: '100%' }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                    />
                  </div>
                  <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-full">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      initial={{ x: '-100%' }}
                      animate={{ x: '100%' }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </div>
);

export default SkeletonLoader; 

// Loading Overlay Skeleton
export const LoadingOverlaySkeleton = ({ message = "Loading..." }) => (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl max-w-md w-full mx-4">
      <div className="flex flex-col items-center space-y-4">
        {/* Skeleton for loading content */}
        <div className="relative overflow-hidden w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
          />
        </div>
        
        {/* Skeleton text lines */}
        <div className="space-y-2 w-full">
          <div className="relative overflow-hidden h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
          <div className="relative overflow-hidden h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Small Loading Skeleton for inline loading states
export const SmallLoadingSkeleton = ({ size = "w-4 h-4" }) => (
  <div className={`relative overflow-hidden ${size} bg-gray-200 dark:bg-gray-700 rounded-full`}>
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
      initial={{ x: '-100%' }}
      animate={{ x: '100%' }}
      transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
    />
  </div>
);

// Button Loading Skeleton
export const ButtonLoadingSkeleton = ({ className = "" }) => (
  <div className={`relative overflow-hidden w-5 h-5 bg-white/20 rounded-full ${className}`}>
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
      initial={{ x: '-100%' }}
      animate={{ x: '100%' }}
      transition={{ duration: 1.5, repeat: Infinity, ease: [0.4, 0.0, 0.2, 1] }}
    />
  </div>
); 